/**
 * Freio de tentativas de login: limite por janela, atraso progressivo e
 * bloqueio temporário da conta.
 *
 * ## Isto NÃO é a defesa contra força bruta — e precisa estar escrito aqui
 *
 * Tudo nesta tela roda no navegador de quem está tentando entrar. Quem ataca
 * não abre a tela: manda `POST /auth/login` direto, e nada daqui existe para
 * ele. A defesa de verdade é no servidor, em
 * `services/api/src/auth/auth.controller.ts`, contando por e-mail e por IP — e
 * **ela ainda não existe**. Enquanto não existir, o que este arquivo entrega é:
 *
 *   - **freio para gente de verdade** — o operador que errou a senha três vezes
 *     para de martelar o botão e vai procurar a senha certa;
 *   - **mensagem honesta** — "faltam 2 tentativas" em vez de um 401 repetido;
 *   - **o contrato da regra em um lugar só**, testado, para o servidor adotar
 *     os mesmos números quando a contrapartida for escrita.
 *
 * Não confunda o freio com a tranca. Esta é o freio.
 *
 * ## Por que `localStorage` e não `sessionStorage`
 *
 * A sessão do operador vive em `sessionStorage` de propósito (fechou a aba,
 * acabou). O freio é o contrário: se ele morresse no F5, bastaria recarregar a
 * página para zerar a contagem, e aí ele não freia nem o humano distraído.
 * Aqui não há segredo nenhum guardado — só contadores.
 *
 * ## Por que o e-mail é guardado como número
 *
 * A chave é um hash do e-mail, não o e-mail. O painel assume máquina
 * compartilhada (é o que motiva o `sessionStorage` da sessão), e uma lista de
 * e-mails de operador em `localStorage` seria um brinde para quem senta na
 * mesma máquina depois. Hash de 32 bits colide em teoria; a consequência de uma
 * colisão aqui é alguém esperar 10 minutos sem motivo, não uma falha de
 * segurança.
 */

/** Falhas consecutivas que disparam o bloqueio. */
export const MAX_FAILURES = 5;
/** Quanto dura o bloqueio da conta. */
export const LOCK_MS = 10 * 60 * 1000;
/** Teto de tentativas por janela, no navegador inteiro (todos os e-mails). */
export const RATE_LIMIT = 10;
export const RATE_WINDOW_MS = 60 * 1000;
/** Teto do atraso progressivo — sem ele, a 4ª falha já pediria meio minuto. */
export const MAX_DELAY_MS = 8_000;

const STORAGE_KEY = "mt.login-guard";

/**
 * Atraso antes da PRÓXIMA tentativa, por número de falhas consecutivas.
 *
 * 1 s, 2 s, 4 s, 8 s. A primeira falha é de graça: errar a senha uma vez é o
 * caso comum, e punir isso com espera só ensina o operador a odiar a tela.
 */
export function delayAfter(failures: number): number {
  if (failures <= 0) return 0;
  return Math.min(2 ** (failures - 1) * 1000, MAX_DELAY_MS);
}

export type Reason = "ok" | "locked" | "delay" | "rate";

export interface Decision {
  allowed: boolean;
  reason: Reason;
  /** Quanto falta esperar, em ms. `0` quando `allowed`. */
  waitMs: number;
  /** Quantas tentativas ainda cabem antes do bloqueio. */
  remainingAttempts: number;
  failures: number;
}

interface Entry {
  failures: number;
  lockedUntil: number;
  nextAllowedAt: number;
  updatedAt: number;
}

interface Snapshot {
  accounts: Record<string, Entry>;
  /** Carimbos das tentativas recentes — a janela deslizante do limite. */
  window: number[];
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const EMPTY: Snapshot = { accounts: {}, window: [] };

/** FNV-1a de 32 bits. Não é criptografia: é só para não guardar o e-mail. */
export function keyOf(email: string): string {
  let hash = 0x811c9dc5;
  const normalized = email.trim().toLowerCase();
  for (let i = 0; i < normalized.length; i++) {
    hash ^= normalized.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

export class LoginGuard {
  private readonly now: () => number;
  private readonly storage: StorageLike | null;
  /** Usado quando o navegador bloqueia armazenamento (janela anônima). */
  private memory: Snapshot = structuredClone(EMPTY);

  constructor(opts: { now?: () => number; storage?: StorageLike | null } = {}) {
    this.now = opts.now ?? (() => Date.now());
    this.storage = opts.storage === undefined ? safeLocalStorage() : opts.storage;
  }

  /** Pode tentar agora? Não muda nada — é consulta. */
  check(email: string): Decision {
    const snapshot = this.read();
    const now = this.now();
    const entry = this.entryOf(snapshot, email, now);

    if (entry.lockedUntil > now) {
      return { allowed: false, reason: "locked", waitMs: entry.lockedUntil - now, remainingAttempts: 0, failures: entry.failures };
    }
    if (entry.nextAllowedAt > now) {
      return {
        allowed: false,
        reason: "delay",
        waitMs: entry.nextAllowedAt - now,
        remainingAttempts: Math.max(0, MAX_FAILURES - entry.failures),
        failures: entry.failures,
      };
    }

    // Limite por janela: conta TODAS as tentativas do navegador, inclusive as
    // que deram certo. É o que impede varrer uma lista de e-mails usando uma
    // tentativa por conta, que passaria pelo bloqueio por conta sem encostar
    // nele.
    const recentes = snapshot.window.filter((t) => t > now - RATE_WINDOW_MS);
    if (recentes.length >= RATE_LIMIT) {
      const maisAntiga = Math.min(...recentes);
      return {
        allowed: false,
        reason: "rate",
        waitMs: Math.max(0, maisAntiga + RATE_WINDOW_MS - now),
        remainingAttempts: Math.max(0, MAX_FAILURES - entry.failures),
        failures: entry.failures,
      };
    }

    return { allowed: true, reason: "ok", waitMs: 0, remainingAttempts: Math.max(0, MAX_FAILURES - entry.failures), failures: entry.failures };
  }

  /** Registra que uma tentativa foi DISPARADA (deu certo ou não). */
  recordAttempt(): void {
    const snapshot = this.read();
    const now = this.now();
    snapshot.window = [...snapshot.window.filter((t) => t > now - RATE_WINDOW_MS), now].slice(-RATE_LIMIT * 2);
    this.write(snapshot);
  }

  /**
   * Credencial recusada pelo servidor.
   *
   * Só chame com recusa de credencial (401). Rede caída e 500 **não** contam:
   * trancar o operador porque o Wi-Fi oscilou é transformar um problema de
   * infraestrutura em dez minutos parado.
   */
  recordFailure(email: string): Decision {
    const snapshot = this.read();
    const now = this.now();
    const entry = this.entryOf(snapshot, email, now);

    entry.failures += 1;
    entry.updatedAt = now;
    if (entry.failures >= MAX_FAILURES) {
      entry.lockedUntil = now + LOCK_MS;
      entry.nextAllowedAt = entry.lockedUntil;
    } else {
      entry.nextAllowedAt = now + delayAfter(entry.failures);
    }
    snapshot.accounts[keyOf(email)] = entry;
    this.write(snapshot);

    return entry.lockedUntil > now
      ? { allowed: false, reason: "locked", waitMs: entry.lockedUntil - now, remainingAttempts: 0, failures: entry.failures }
      : {
          allowed: false,
          reason: "delay",
          waitMs: Math.max(0, entry.nextAllowedAt - now),
          remainingAttempts: Math.max(0, MAX_FAILURES - entry.failures),
          failures: entry.failures,
        };
  }

  /** Entrou. Zera a conta — e só a conta: a janela segue valendo. */
  recordSuccess(email: string): void {
    const snapshot = this.read();
    delete snapshot.accounts[keyOf(email)];
    this.write(snapshot);
  }

  /** Só para teste e para o botão de "esqueci": apaga tudo. */
  reset(): void {
    this.memory = structuredClone(EMPTY);
    try {
      this.storage?.removeItem(STORAGE_KEY);
    } catch {
      /* armazenamento bloqueado: a memória já foi limpa */
    }
  }

  // ------------------------------------------------------------- interno

  private entryOf(snapshot: Snapshot, email: string, now: number): Entry {
    const entry = snapshot.accounts[keyOf(email)] ?? { failures: 0, lockedUntil: 0, nextAllowedAt: 0, updatedAt: now };

    // Relógio que andou para trás (fuso, NTP, ou alguém mexendo) deixaria um
    // bloqueio de anos. Fora do horizonte possível, o registro não é confiável
    // e o certo é destravar — errar liberando é melhor que trancar para sempre
    // numa conta legítima.
    if (entry.lockedUntil - now > LOCK_MS || entry.nextAllowedAt - now > LOCK_MS) {
      return { failures: 0, lockedUntil: 0, nextAllowedAt: 0, updatedAt: now };
    }
    // Bloqueio vencido zera a contagem: as 5 falhas antigas já foram pagas, e
    // somá-las às próximas bloquearia na primeira falha do dia seguinte.
    if (entry.lockedUntil > 0 && entry.lockedUntil <= now) {
      return { failures: 0, lockedUntil: 0, nextAllowedAt: 0, updatedAt: now };
    }
    return { ...entry };
  }

  private read(): Snapshot {
    if (!this.storage) return this.memory;
    try {
      const raw = this.storage.getItem(STORAGE_KEY);
      if (!raw) return structuredClone(EMPTY);
      const parsed = JSON.parse(raw) as Partial<Snapshot>;
      return {
        accounts: typeof parsed.accounts === "object" && parsed.accounts !== null ? parsed.accounts : {},
        window: Array.isArray(parsed.window) ? parsed.window.filter((t) => typeof t === "number") : [],
      };
    } catch {
      // Conteúdo corrompido (ou editado à mão) não pode derrubar a tela de
      // login — sem login não há como consertar nada.
      return structuredClone(EMPTY);
    }
  }

  private write(snapshot: Snapshot): void {
    this.memory = snapshot;
    if (!this.storage) return;
    try {
      this.storage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch {
      /* cota cheia ou armazenamento bloqueado: segue valendo só nesta aba */
    }
  }
}

function safeLocalStorage(): StorageLike | null {
  try {
    const probe = "mt.probe";
    globalThis.localStorage.setItem(probe, "1");
    globalThis.localStorage.removeItem(probe);
    return globalThis.localStorage;
  } catch {
    // Janela anônima, cookies bloqueados, iframe sem permissão: cai para
    // memória. O freio continua valendo enquanto a aba estiver aberta.
    return null;
  }
}

/** `9:58`, `45 s`. O texto que vai para a tela. */
export function formatWait(ms: number): string {
  const segundos = Math.max(0, Math.ceil(ms / 1000));
  if (segundos < 60) return `${segundos} s`;
  return `${Math.floor(segundos / 60)}:${String(segundos % 60).padStart(2, "0")}`;
}

/** A mensagem de cada motivo, já com o tempo. */
export function explain(decision: Decision): string | null {
  if (decision.allowed) return null;
  const espera = formatWait(decision.waitMs);
  if (decision.reason === "locked") {
    return `Muitas tentativas. Acesso bloqueado por ${espera}. O bloqueio é deste navegador — se a senha foi esquecida, procure o administrador.`;
  }
  if (decision.reason === "rate") {
    return `Tentativas demais em pouco tempo. Aguarde ${espera}.`;
  }
  return `Aguarde ${espera} antes de tentar de novo.`;
}
